import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { CreditCard, Link2, Pencil, Plus, RotateCcw, UserX } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMe } from '../../api/auth';
import { PowersensorButton } from '../powersensor/PowersensorPanels'; // Powersensor is being retired
import { ApiError } from '../../api/client';
import { useDeactivateDriver, useDrivers, useUpdateDriver, type Driver } from '../../api/drivers';
import { useOrganisations } from '../../api/organisations';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formatAgo, formatDate } from '../../lib/format';
import { colours, radius } from '../../theme';
import { DriverDialog, type DriverTab } from './DriverDialog';
import { OnboardingLinksDialog } from './OnboardingLinksDialog';

type Show = 'active' | 'review' | 'inactive';
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Drivers and their RFID cards. Sign-ups from onboarding links wait at the top. */
export function DriversPage() {
  const drivers = useDrivers('all');
  const orgs = useOrganisations();
  const { data: me } = useMe();
  const canEdit = usePermission('drivers.edit');
  const dialog = useDialog();
  const toast = useToast();
  const deactivate = useDeactivateDriver();
  const update = useUpdateDriver();
  const [params, setParams] = useSearchParams();
  const [show, setShow] = useState<Show>('active');
  const [org, setOrg] = useState('all');
  const manyOrgs = me?.role === 'admin' || (me?.organisationIds?.length ?? 0) > 1;

  const all = drivers.data ?? [];
  const waiting = all.filter((d) => d.isActive && d.needsReview);
  const active = all.filter((d) => d.isActive);
  const inactiveCount = all.length - active.length;
  const rows = useMemo(
    () =>
      all.filter(
        (d) =>
          (org === 'all' || d.organisationId === org) &&
          (show === 'active' ? d.isActive : show === 'review' ? d.isActive && d.needsReview : !d.isActive),
      ),
    [all, org, show],
  );

  const open = (id?: string, initialTab?: DriverTab) => void dialog.open(DriverDialog, { driverId: id, initialTab });

  // From the "waiting for approval" email: /drivers?review=<id>
  const reviewId = params.get('review');
  useEffect(() => {
    if (!reviewId) return;
    setParams({}, { replace: true });
    open(reviewId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong. Please try again.', 'error');
    }
  };

  const cardTotal = active.reduce((n, d) => n + d.cardCount, 0);
  const lead = drivers.isPending ? ' ' : active.length === 0 ? 'No drivers yet.' : `${plural(active.length, 'driver')} with ${plural(cardTotal, 'card')}.`;

  const columns: Column<Driver>[] = [
    {
      key: 'name',
      header: 'Driver',
      sortValue: (d) => d.name.toLowerCase(),
      render: (d) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{d.name}</Typography>
          <Typography variant="body2" color="textSecondary">{d.email}</Typography>
        </Box>
      ),
    },
    ...(manyOrgs ? [{ key: 'org', header: 'Organisation', hideBelow: 'md' as const, sortValue: (d: Driver) => d.organisationName.toLowerCase(), render: (d: Driver) => d.organisationName }] : []),
    {
      key: 'vehicle',
      header: 'Vehicle',
      hideBelow: 'md',
      sortValue: (d) => d.vehicleType?.toLowerCase() ?? null,
      render: (d) =>
        d.vehicleType || d.registration ? (
          <Box>
            <Typography>{d.vehicleType ?? ' '}</Typography>
            <Typography variant="body2" color="textSecondary">{d.registration ?? ' '}</Typography>
          </Box>
        ) : (
          <Typography color="textSecondary">—</Typography>
        ),
    },
    { key: 'cards', header: 'Cards', align: 'right', sortValue: (d) => d.cardCount, render: (d) => <span className="num">{d.cardCount}</span> },
    {
      key: 'last',
      header: 'Last charged',
      hideBelow: 'sm',
      sortValue: (d) => d.lastSessionAt,
      render: (d) => (d.lastSessionAt ? formatAgo(d.lastSessionAt) : <Typography color="textSecondary">Never</Typography>),
    },
    ...(show !== 'active'
      ? [
          {
            key: 'status',
            header: 'Status',
            render: (d: Driver) =>
              !d.isActive ? <StatusLabel tone="off">Deactivated</StatusLabel> : d.needsReview ? <StatusLabel tone="fault">Waiting for approval</StatusLabel> : <StatusLabel tone="success">Active</StatusLabel>,
          },
        ]
      : []),
  ];

  const chip = (value: Show, label: string) => (
    <Chip
      key={value}
      label={label}
      onClick={() => setShow(value)}
      aria-pressed={show === value}
      sx={show === value ? { bgcolor: colours.nearBlack, color: colours.white, '&:hover': { bgcolor: colours.nearBlack } } : { bgcolor: colours.white, border: `1px solid ${colours.hairline}` }}
    />
  );

  return (
    <>
      <PageHeader
        title="Drivers and cards."
        lead={lead}
        actions={
          canEdit && (
            <>
              {(orgs.data ?? []).some((o) => o.powersensorEnabled) && <PowersensorButton />}
              <Button startIcon={<Link2 size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(OnboardingLinksDialog, {})}>
                Onboarding links
              </Button>
              <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => open()}>
                Add a driver
              </Button>
            </>
          )
        }
      />

      {waiting.length > 0 && show !== 'review' && (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mb: 5, borderRadius: `${radius.card}px`, borderColor: colours.nearBlack }}>
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            {waiting.length === 1 ? 'A driver is waiting for approval.' : `${waiting.length} drivers are waiting for approval.`}
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 2.5 }}>
            They filled in their details with an onboarding link. Their cards work once you approve them.
          </Typography>
          <Box sx={{ display: 'grid' }}>
            {waiting.slice(0, 5).map((d) => (
              <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 1.5, borderTop: `1px solid ${colours.hairline}` }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography sx={{ fontWeight: 500 }}>{d.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {[d.email, manyOrgs ? d.organisationName : null, plural(d.cardCount, 'card'), `signed up ${formatDate(d.createdAt)}`].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <Button size="small" variant="contained" onClick={() => open(d.id)}>Review</Button>
              </Box>
            ))}
          </Box>
          {waiting.length > 5 && <Button sx={{ mt: 1.5 }} onClick={() => setShow('review')}>See all {waiting.length}</Button>}
        </Paper>
      )}

      <DataTable
        label="Drivers"
        rows={drivers.isPending ? undefined : rows}
        columns={columns}
        rowKey={(d) => d.id}
        initialSort={{ key: 'name', direction: 'asc' }}
        searchText={(d) => [d.name, d.email, d.phone, d.organisationName, d.vehicleType, d.registration].filter(Boolean).join(' ')}
        searchPlaceholder="Search by name, email or rego"
        toolbar={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' } }}>
            {manyOrgs && (orgs.data?.length ?? 0) > 1 && (
              <TextField select size="small" value={org} onChange={(e) => setOrg(e.target.value)} sx={{ minWidth: 220 }} slotProps={{ htmlInput: { 'aria-label': 'Organisation' } }}>
                <MenuItem value="all">All organisations</MenuItem>
                {(orgs.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.businessName}</MenuItem>)}
              </TextField>
            )}
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }} role="group" aria-label="Show">
              {chip('active', 'Active')}
              {waiting.length > 0 && chip('review', `Waiting for approval (${waiting.length})`)}
              {inactiveCount > 0 && chip('inactive', `Deactivated (${inactiveCount})`)}
            </Stack>
          </Stack>
        }
        onRowClick={(d) => open(d.id)}
        rowActions={(d) => (
          <RowMenu
            label={`Actions for ${d.name}`}
            items={[
              { label: canEdit ? 'Edit' : 'View', icon: Pencil, onClick: () => open(d.id) },
              { label: 'Cards', icon: CreditCard, onClick: () => open(d.id, 'cards') },
              { label: 'Reactivate', icon: RotateCcw, hidden: !canEdit || d.isActive, onClick: () => void run(() => update.mutateAsync({ id: d.id, isActive: true }), `${d.name} is active again.`) },
              {
                label: 'Deactivate',
                icon: UserX,
                danger: true,
                hidden: !canEdit || !d.isActive || d.needsReview,
                onClick: async () => {
                  if (d.chargerCount > 0) {
                    toast(`${d.name} is the plug-and-charge driver on ${d.chargerCount === 1 ? 'a charger' : `${d.chargerCount} chargers`}. Choose someone else there first.`, 'error');
                    return;
                  }
                  const ok = await dialog.confirm({ title: `Deactivate ${d.name}?`, message: 'Their cards stop working. Their sessions stay in reports, and you can reactivate them later.', confirmLabel: 'Deactivate', danger: true });
                  if (ok) await run(() => deactivate.mutateAsync(d.id), `${d.name} was deactivated.`);
                },
              },
            ]}
          />
        )}
        loading={drivers.isPending}
        error={drivers.error}
        onRetry={() => void drivers.refetch()}
        empty={
          all.length === 0 ? (
            <EmptyState
              title="No drivers yet"
              action={
                canEdit && (
                  <Stack direction="row" spacing={1.5}>
                    <Button onClick={() => open()}>Add a driver</Button>
                    <Button onClick={() => void dialog.open(OnboardingLinksDialog, {})}>Send an onboarding link</Button>
                  </Stack>
                )
              }
            >
              Add drivers yourself, or send them an onboarding link to fill in their own details and cards.
            </EmptyState>
          ) : (
            <EmptyState title="Nobody here">No drivers match this filter.</EmptyState>
          )
        }
      />
    </>
  );
}
